import { Activity, useCallback, useEffect, useRef, useState } from 'react'
import {
  Button,
  CircularProgress,
  Grid,
  Paper,
  Typography,
} from '@mui/material'
import {
  FIELD_VIEW_RENDERERS,
  TEXT_FIELD_INPUT_TYPES,
} from '../components/cards/fieldRegistry'
import { useEntityMeta, useSetMeta } from '../context/MetaContext'

import { Add as AddIcon } from '@mui/icons-material'
import HoloTable from '../components/HoloTable'
import fetchWithAuth from '../utilities/fetchWithAuth'
import { getFilterOperators } from '../utilities/gridUtils'
import { kebabToCamel } from '../utilities/stringUtils'
import { resolveNavPath } from '../utilities/pathMapping'
import setUrlParams from '../utilities/setUrlParams'
import { useNavigate } from 'react-router-dom'

const EntityListTemplate = ({
  entityName,
  columns,
  title,
  canAdd = true,
  onRowClick,
  defaultSort = [{ field: 'createdAt', sort: 'desc' }],
  defaultFilters = { items: [] },
  transformData,
  tableProps = {},
  renderItem: RenderItem,
  fetchWithMeta = false,
}) => {
  const [data, setData] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  })
  const [sortModel, setSortModel] = useState(defaultSort)
  const [filterModel, setFilterModel] = useState(defaultFilters)
  const [pageTitle, setPageTitle] = useState(null)

  const entityMeta = useEntityMeta(entityName)
  const setMeta = useSetMeta()
  const navigate = useNavigate()

  const createPath = `/${resolveNavPath(entityName)}/create`
  const metaLoadedRef = useRef(!!entityMeta)

  const fetchData = useCallback(async () => {
    try {
      const params = setUrlParams(
        paginationModel.page,
        paginationModel.pageSize,
        sortModel,
        filterModel,
      )
      const needsMeta = fetchWithMeta && !metaLoadedRef.current
      const { data: response } = await fetchWithAuth(
        `${entityName}?${params.toString()}${needsMeta ? '&meta=true' : ''}`,
      )

      setData(transformData ? transformData(response.data) : response.data)
      setTotalCount(response.pagination.totalItems)
      if (needsMeta) {
        setMeta(entityName, response.meta)
        setPageTitle(response.title)
        metaLoadedRef.current = true
      }
    } catch (error) {
      console.error(`Error fetching ${entityName}:`, error)
    }
  }, [
    paginationModel,
    sortModel,
    filterModel,
    transformData,
    entityName,
    fetchWithMeta,
    setMeta,
  ])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleRowClick = (params) => {
    if (onRowClick) {
      onRowClick(params)
    } else {
      navigate(`/${resolveNavPath(entityName)}/${params.row.uuid}`)
    }
  }

  const pageSize =
    paginationModel.pageSize === 10 ? totalCount : paginationModel.pageSize
  const pageSizeOptions = [5, 10, 25, 100]

  const resolvedColumns =
    columns ??
    (fetchWithMeta && entityMeta
      ? entityMeta.fields
          .filter((f) => entityMeta.meta.options.index.columns.includes(f.key))
          .map((field) => {
            const renderer =
              FIELD_VIEW_RENDERERS[field.type] ?? FIELD_VIEW_RENDERERS.default
            const isFilterable = entityMeta.meta.options.index.filters.includes(
              field.key,
            )
            return {
              field: field.key,
              headerName: field.label,
              sortable: isFilterable,
              filterable: isFilterable,
              filterOperators: getFilterOperators(
                TEXT_FIELD_INPUT_TYPES[field.type] ?? field.type,
              ),
              flex: 1,
              width: 160,
              ...(FIELD_VIEW_RENDERERS[field.type] && {
                renderCell: (params) =>
                  renderer({
                    value: params.row[field.key],
                    options: field.options,
                    path: field.path,
                  }),
              }),
            }
          })
      : [])

  const actionButtons = {
    add: (
      <Button
        variant="contained"
        key="add-entity"
        startIcon={<AddIcon />}
        onClick={() => navigate(createPath)}
      >
        Add
      </Button>
    ),
  }

  return (
    <Grid container size={12} spacing={2}>
      <Grid container size={12}>
        <Grid size={6}>
          <Typography
            variant="h6"
            component="h1"
            margin={'auto'}
            justifyContent={'start'}
          >
            {pageTitle ?? title}
          </Typography>
        </Grid>
        <Grid size={6} textAlign={'right'}>
          <Activity mode={canAdd && !entityMeta ? 'visible' : 'hidden'}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate(createPath)}
            >
              Add
            </Button>
          </Activity>
          {entityMeta?.meta.options.index.actions?.map(
            (action) => actionButtons[action],
          )}
        </Grid>
      </Grid>
      <Grid size={12}>
        {RenderItem ? (
          <Grid container spacing={2}>
            {data.map((item) => (
              <Grid key={item.uuid} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <RenderItem
                  {...{ [kebabToCamel(entityName.slice(0, -1))]: item }}
                />
              </Grid>
            ))}
          </Grid>
        ) : (fetchWithMeta && !entityMeta) || !resolvedColumns.length ? (
          <Paper sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Paper>
        ) : (
          <Paper>
            <HoloTable
              id={entityName}
              data={data}
              columns={resolvedColumns}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              sortModel={sortModel}
              onSortModelChange={setSortModel}
              filterModel={filterModel}
              onFilterModelChange={setFilterModel}
              rowCount={totalCount}
              onRowClick={handleRowClick}
              pageSize={pageSize}
              pageSizeOptions={pageSizeOptions}
              {...tableProps}
            />
          </Paper>
        )}
      </Grid>
    </Grid>
  )
}

export default EntityListTemplate
